// src/components/CertInexistenciaTemplate.jsx
import React, { useMemo } from "react";
import logo from "../assets/DesarrollandoTerritorios.png"

function formatDateDDMMYYYY(dateLike) {
  const d = dateLike ? new Date(dateLike) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ✅ Construye URL de firma sin amarrarse a presupuesto
function buildFirmaUrl(rawUrl) {
  if (!rawUrl) return "";
  const url = String(rawUrl).trim();
  if (!url) return "";

  // base64
  if (url.startsWith("data:image/")) return url;

  // absoluta
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // linux path (si te llega de servidor)
  if (url.startsWith("/opt/uploads/")) return url.replace("/opt/uploads/", "/uploads/");

  // ya es web path
  if (url.startsWith("/uploads/")) return url;

  // relativa: por defecto la asumimos en /uploads/inexistencia/
  if (!url.startsWith("/")) return `/uploads/inexistencia/${url}`;

  return url;
}

export default function CertInexistenciaTemplate({ data = {}, firmaPreview = "", roleId = 9 }) {
  const objeto = useMemo(() => {
    const raw = data?.objeto_snapshot ?? data?.objeto ?? "";
    return String(raw || "").trim();
  }, [data]);

  const fechaElaboro = useMemo(() => formatDateDDMMYYYY(new Date()), []);

  // Datos del validador (tabla)
  const ELABORO_NOMBRE = "PATRICIA VERGARA ALVAREZ";
  const ELABORO_CARGO = "Profesional Universitaria (E)";

  // Firma Secretario (centrada)
  const FIRMA_SECRETARIA_NOMBRE = "ALEJANDRA VALBUENA URIBE";
  const FIRMA_SECRETARIA_LINEA1 = "Secretaria de Despacho";
  const FIRMA_SECRETARIA_LINEA2 = "Secretaría de Servicios Administrativos";

  // ✅ Firma guardada del secretario (BD)
  const firmaSecretarioGuardada = buildFirmaUrl(data?.firma_secretario_url || "");

  // ✅ Firma guardada del validador (BD)
  const firmaRevisorGuardada = buildFirmaUrl(data?.firma_revisor_url || "");

  // ✅ Preview:
  // - Si rol 10 está firmando, el preview es del secretario (para que el PDF salga con esa firma).
  // - Si rol 9 está firmando, el preview es del validador.
  const firmaPreviewUrl = buildFirmaUrl(firmaPreview || "");

  const firmaSecretarioUrl =
    firmaSecretarioGuardada || (roleId === 10 ? firmaPreviewUrl : "");

  const firmaRevisorUrl =
    firmaRevisorGuardada || (roleId === 9 ? firmaPreviewUrl : "");

  const styles = {
    wrapper: {
      width: "100%",
      background: "#fff",
      borderRadius: 12,
      padding: 18,
      fontSize: 15,
      lineHeight: 1.35,
      color: "#111827",
    },

    headerRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    },

    logo: {
      width: 70,
      height: 70,
      objectFit: "contain",
    },

    headerTextWrap: {
      flex: 1,
      textAlign: "center",
      paddingRight: 70, // compensa el logo a la izquierda para mantener centrado
    },

    title: {
      textAlign: "center",
      fontWeight: 800,
      fontSize: 16.5,
      marginBottom: 10,
    },
    headerCenter: { textAlign: "center", marginTop: 4, fontWeight: 800 },
    subHeaderCenter: { textAlign: "center", marginTop: 2, fontWeight: 800 },
    bodyText: { marginTop: 10, fontSize: 14.5 },

    firmaContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: 18,
    },
    firmaBlock: {
      textAlign: "center",
      maxWidth: "400px",
      width: "100%",
    },

    firmaImgWrap: {
      height: 70,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      margin: "0 auto 6px",
      width: "86%",
    },
    firmaImgAboveLine: {
      maxHeight: 70,
      maxWidth: "100%",
      objectFit: "contain",
      display: "block",
    },

    firmaLinea: {
      borderTop: "2px solid #111827",
      margin: "0 auto 8px",
      width: "86%",
    },
    firmaNombre: { fontWeight: 900, marginBottom: 2 },
    firmaCargo: { fontSize: 13.5, color: "#111827" },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 14,
      fontSize: 13.5,
    },
    th: {
      border: "1px solid #111827",
      padding: "8px 6px",
      textAlign: "center",
      fontWeight: 900,
      background: "#fff",
    },
    td: {
      border: "1px solid #111827",
      padding: "10px 6px",
      verticalAlign: "middle",
      fontSize: 13.5,
    },
    firmaImgTable: {
      maxHeight: 42,
      maxWidth: 160,
      objectFit: "contain",
      display: "block",
      margin: "0 auto",
    },
    objetoBoldInline: { fontWeight: 900 },
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.headerRow}>
        <img
          src={logo}
          alt="Copacabana"
          style={styles.logo}
          crossOrigin="anonymous"
        />
        <div style={styles.headerTextWrap}>
          <div style={styles.title}>Certificación de Inexistencia</div>

          <div style={styles.headerCenter}>
            LA SECRETARÍA DE SERVICIOS ADMINISTRATIVOS DE LA ALCALDIA
          </div>
          <div style={styles.subHeaderCenter}>DE COPACABANA</div>
          <div style={{ ...styles.headerCenter, marginTop: 10 }}>CERTIFICA QUE:</div>
        </div>
        <div style={{ width: 70 }} />
      </div>

      <div style={styles.bodyText}>
        Teniendo en cuenta las necesidades de la entidad, y luego de revisado el manual de
        funciones adoptado mediante Decreto Municipal 099 de 2023 frente a los estudios previos
        para "<span style={styles.objetoBoldInline}>{objeto || "—"}</span>". Esta secretaría
        manifiesta que no se encuentran asignadas las actividades descritas a ningún funcionario
        de planta y tampoco existe ningún empleado con la experticia o conocimiento especializado
        para ejecutarlas.
      </div>

      <br />
      <br />

      <div style={{ marginTop: 10, fontSize: 14.5 }}>
        Dada en Copacabana, 
      </div>

      <br />
      <br />
      <br />

      {/* ✅ Firma única centrada del Secretario */}
      <div style={styles.firmaContainer}>
        <div style={styles.firmaBlock}>
          <div style={styles.firmaImgWrap}>
            {firmaSecretarioUrl ? (
              <img
                src={firmaSecretarioUrl}
                alt="Firma Secretaria"
                crossOrigin="anonymous"
                style={styles.firmaImgAboveLine}
              />
            ) : null}
          </div>

          <div style={styles.firmaLinea} />
          <div style={styles.firmaNombre}>{FIRMA_SECRETARIA_NOMBRE}</div>
          <div style={styles.firmaCargo}>{FIRMA_SECRETARIA_LINEA1}</div>
          <div style={styles.firmaCargo}>{FIRMA_SECRETARIA_LINEA2}</div>
        </div>
      </div>

      <br />
      <br />
      <br />

      {/* Tabla Elaboró y Proyectó (validador) */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>PROPÓSITO</th>
            <th style={styles.th}>NOMBRE</th>
            <th style={styles.th}>CARGO</th>
            <th style={styles.th}>FECHA</th>
            <th style={styles.th}>FIRMA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...styles.td, textAlign: "left" }}>Elaboró y Proyectó</td>
            <td style={{ ...styles.td, fontWeight: 900 }}>{ELABORO_NOMBRE}</td>
            <td style={{ ...styles.td }}>{ELABORO_CARGO}</td>
            <td style={{ ...styles.td, textAlign: "center" }}>{fechaElaboro}</td>
            <td style={{ ...styles.td, textAlign: "center" }}>
              {firmaRevisorUrl ? (
                <img
                  src={firmaRevisorUrl}
                  alt="Firma Validador"
                  crossOrigin="anonymous"
                  style={styles.firmaImgTable}
                />
              ) : (
                <span style={{ opacity: 0.65 }}>—</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
